import { useCallback, useEffect, useRef, useState, FormEvent, ReactNode } from "react"
import { CloseButton } from "./CloseButton"
import { classNames } from "../Components"
import { Icon, Icons } from "./Icon"
import { logger } from "../App/Logger"

export interface EditGroupFieldDefinition {
  array?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  default?: any;
  name: string;
  type: string;
  fields?: EditGroupFieldDefinitions[];
}

export type EditGroupFieldDefinitions = string | EditGroupFieldDefinition;

type EditGroupDataField = string | number | boolean | null;

export interface EditGroupData {
  [name: string]: EditGroupDataField | EditGroupDataField[] | EditGroupData | EditGroupData[];
}

type EditGroupOnSaveCallback = (data: FormData, values: EditGroupData) => Promise<boolean | void>;
type EditGroupOnValidateCallback = (data: FormData, values: EditGroupData) => boolean;
// Called with the rejected `onSave` error whenever a save fails, and with `undefined`
// at the start of every new submit attempt (so a call site can clear a stale error
// before a retry, e.g. right when the user edits again) -- see `editNotAllowedFields`
// for extracting a `logs.update`/`days.update` field-rejection list out of the error.
type EditGroupOnSaveErrorCallback = (error: unknown) => void;

interface EditGroupProps {
  buttonFirst?: boolean;
  changed?: boolean;
  children: ReactNode;
  className?: string;
  editable?: boolean;
  editableFields?: EditGroupFieldDefinitions[];
  editing?: boolean;
  error?: string;
  hideButton?: boolean;
  // Issue #1142 (broader fix): consulted once per top-level field name in
  // `formValues()` -- any field for which this returns `false` is omitted from the
  // submitted values entirely, regardless of what value the (possibly stale,
  // display-rounded) `readOnly` input happens to carry for it. When omitted,
  // `formValues()` submits every field (unchanged legacy behavior).
  isFieldEditable?: (name: string) => boolean;
  manualSave?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onCancel?: any;
  onSave: EditGroupOnSaveCallback;
  onSaveError?: EditGroupOnSaveErrorCallback;
  onValidate?: EditGroupOnValidateCallback;
}

export function convertInput(type: string, value?: FormDataEntryValue | null): EditGroupDataField {
  // TODO: Handle instanceof File
  if (!value || typeof value != "string") {
    return null;
  }
  switch (type) {
    case "float": {
      const decimalPoint = (1.1).toLocaleString(navigator.language).substr(1, 1) || ".";
      const f = decimalPoint == "," ? parseFloat(value.replaceAll(",", ".")) : parseFloat(value);
      return isNaN(f) ? null : f;
    }
    case "int": {
      const i = parseInt(value);
      return isNaN(i) ? null : i;
    }
    case "bool":
      return value == "true";
    case "timezone": {
      if (value == "UTC") {
        return 0;
      }
      // The label carries an optional `:MM` part ("UTC-9:30"), whose sign follows
      // the hours; parsing only the leading integer used to truncate it to -9 h.
      const parts = /^UTC([+-])(\d+)(?::(\d+))?$/.exec(value.trim());
      if (!parts) {
        return null;
      }
      const magnitude = parseInt(parts[2]) * 3600 + (parts[3] ? parseInt(parts[3]) * 60 : 0);
      return parts[1] == "-" ? -magnitude : magnitude;
    }
    default:
      return value;
  }
}

enum EditGroupSaveState {
  noChange,
  saved,
  saving,
  failed,
}

// Issue #1142 (broader fix): `isFieldEditable`, when given, is consulted once per
// top-level field name -- a field it disallows is skipped entirely, before any
// scalar/array/object-specific reading of `data`, so a currently non-editable field
// can never be resubmitted, even accidentally via a stale or display-rounded
// `readOnly` input value. Nested `fields` (object arrays) are not separately
// gated -- `isFieldEditable` only applies to the top-level fields passed in here.
function formValues(data: FormData, prefix: string, editableFields: EditGroupFieldDefinitions[], isFieldEditable?: (name: string) => boolean): EditGroupData {
  const values: EditGroupData = {};
  for (const field of editableFields) {
    const name = typeof field == "string" ? field : field.name;
    if (isFieldEditable && !isFieldEditable(name)) {
      continue;
    }
    if (typeof field == "string") {
      values[field] = convertInput("string", data.get(`${prefix}${field}`));
    } else if (field.array == true) {
      if (field.type == "object" && field.fields) {
        values[field.name] = [];
        const a: EditGroupData[] = [];
        let i = 0;
        while (data.get(`${prefix}${field.name}[${i}].id`) != undefined) {
          a[i] = formValues(data, `${prefix}${field.name}[${i}].`, field.fields);
          i += 1;
        }
        values[field.name] = a;
      } else {
        const a: EditGroupDataField[] = [];
        let i = 0;
        let v;
        const max = parseInt((data.get(`${prefix}${field.name}.length`) || "-1").toString());
        if (max >= 0) {
          // An explicit zero-length marker is itself a genuine (empty) value --
          // e.g. the view intentionally declaring "no entries" -- so it's always
          // submitted even though the loop below never runs for it.
          let found = max == 0;
          for (i = 0; i < max; i += 1) {
            v = data.get(`${prefix}${field.name}[${i}]`);
            const c = convertInput(field.type, v);
            if (c != null) {
              found = true;
            }
            a[i] = c == null ? field.default : c;
          }
          // Issue #1142 (follow-up): `NavigationView` also renders this `.length`
          // marker whenever an engine is currently on (so a manually-typed rpm can
          // be submitted), not only when there's real stored data. A freshly
          // recorded log with an engine switched on but no rpm typed yet still
          // renders a real (blank) `<input>` for it, which submits an empty
          // string for every entry -- converting to `null` and falling back to
          // `field.default` throughout. That produced a fully fabricated array
          // (e.g. `[-1]`) that is NOT the same value as the true nil stored
          // value, again rejecting the *entire* save. When nothing was genuinely
          // entered anywhere in a length-bounded array either, omit it entirely --
          // same reasoning as the open-ended branch below.
          if (found) {
            values[field.name] = a;
          }
        } else {
          while ((v = data.get(`${prefix}${field.name}[${i}]`)) != undefined) {
            const c = convertInput(field.type, v);
            a[i] = c == null ? field.default : c;
            i += 1;
          }
          // No `.length` field and no indexed entries at all -- the view rendered
          // nothing for this array field, so leave it out of `values` entirely
          // instead of defaulting to `[]`. `EditGroup` always submits the whole
          // form, and a same-value resubmission of an untouched field must never
          // count as an edit attempt (see the scalar-field handling below) -- but an
          // empty array is NOT the same value as an absent/nil array server-side, so
          // submitting `[]` here would register as a spurious "changed" edit
          // whenever the true stored value is nil (issue #1142: this rejected the
          // *entire* save -- e.g. an unrelated Comment edit -- for any freshly
          // recorded log whose `engineRpms` had never been set).
          if (i > 0) {
            values[field.name] = a;
          }
        }
      }
    } else if (field.type == "object" && field.fields) {
      values[field.name] = formValues(data, `${prefix}${field.name}.`, field.fields);
    } else if (field.name) {
      const v = convertInput(field.type, data.get(`${prefix}${field.name}`));
      values[field.name] = (v == null || v == undefined) ? field.default : v;
    }
  }
  return values;
}

export const EditGroupData = { convertInput, formValues };

// Loosely-parses a `logs.update`/`days.update` `EditNotAllowedError` response body
// (`Sources/LogbookApi/Api.swift`'s `EditNotAllowedResponse` -- deliberately not
// typegen'd, like every other rejected-call error body) out of an `onSave` rejection.
// Returns `undefined` for any other error shape (network failure, an unrelated 4xx/5xx,
// etc.) so callers can fall back to a generic message.
export function editNotAllowedFields(error: unknown): string[] | undefined {
  if (
    error != null && typeof error == "object" &&
    "error" in error && error.error == "editNotAllowed" &&
    "fields" in error && Array.isArray(error.fields) &&
    error.fields.every((f) => typeof f == "string")
  ) {
    return error.fields as string[];
  }
  return undefined;
}

// Builds a user-facing save-error message for `EditGroup`'s `error` prop out of a
// rejected `onSave` error (or `undefined` for no error). When `error` is an
// `EditNotAllowedError` (issue #529), names the rejected fields via `fieldLabels`
// (falling back to the raw field name for anything missing from the map, and
// deduping labels shared by multiple fields, e.g. a combined "Wind" label for both
// `windDirection` and `windSpeed`); any other error shape (network failure,
// unrelated 4xx/5xx, etc.) falls back to `messages.generic`.
export function saveErrorMessage(error: unknown, fieldLabels: Record<string, string>, messages: { fields: (labels: string) => string; generic: string }): string | undefined {
  if (error === undefined) {
    return undefined;
  }
  const fields = editNotAllowedFields(error);
  if (fields && fields.length > 0) {
    const labels = Array.from(new Set(fields.map((f) => fieldLabels[f] ?? f)));
    return messages.fields(labels.join(", "));
  }
  return messages.generic;
}

export function EditGroup({ buttonFirst, changed, children, className, editable, editableFields, editing, error, hideButton, isFieldEditable, manualSave, onCancel, onSave, onSaveError, onValidate }: EditGroupProps) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [saveState, setSaveState] = useState(EditGroupSaveState.noChange);

  const submit = useCallback(async (e: SubmitEvent | FormEvent<HTMLFormElement>) => {
    if (e.preventDefault) {
      e.preventDefault();
    }
    const target = formRef.current;
    if (!target) {
      return;
    }
    const data = new FormData(target);
    const values: EditGroupData = editableFields ? EditGroupData.formValues(data, "", editableFields, isFieldEditable) : {};
    setSaveState(EditGroupSaveState.saving);
    // Clear any error left over from a previous failed save before this attempt --
    // otherwise a stale "rejected" message could linger through a subsequent retry
    // that hasn't resolved yet (or has since succeeded).
    onSaveError?.(undefined);
    try {
      const result = await onSave(data, values);
      if (result == true) {
        formRef.current?.reset();
      }
      setSaveState(EditGroupSaveState.saved);
    } catch (saveError) {
      // Revert the whole form on a rejected save. `isFieldEditable` (above) already
      // keeps every currently non-editable field out of `values` on the normal
      // path, and `logs.update`/`days.update` now silently drop any disallowed
      // field rather than rejecting the request (issue #1142), so this only fires
      // for a genuine failure (network, auth, server error, ...) -- not for an
      // edit-permissiveness rejection. Still reset on any failure so the user's
      // on-screen edit never looks "saved" when it wasn't, and so EditGroup's own
      // auto-resubmit effect below doesn't retry the same failing save forever,
      // since nothing else here clears `changed`.
      formRef.current?.reset();
      setSaveState(EditGroupSaveState.failed);
      onSaveError?.(saveError);
      logger.error(saveError);
    }
  }, [editableFields, formRef, isFieldEditable, onSave, onSaveError]);
  const button = editable && changed && !hideButton && <button className={classNames(["EditGroup-save-button", manualSave ? "enter" : "refresh"])}><Icon icon={manualSave ? Icons.Enter : Icons.Refresh} /></button>;

  useEffect(() => {
    if (changed && saveState != EditGroupSaveState.saving && !manualSave) {
      submit(new SubmitEvent("submit", { submitter: formRef.current })).catch((error) => {
        logger.error(error);
      });
    } else if (changed && manualSave && onValidate && formRef.current) {
      const data = new FormData(formRef.current);
      const values = editableFields ? EditGroupData.formValues(data, "", editableFields, isFieldEditable) : {};
      onValidate(data, values);
    }
  }, [changed, formRef, saveState, manualSave, editableFields, isFieldEditable]);

  return (
    <div className={classNames([`EditGroup`, className])}>
      <form ref={formRef} onSubmit={submit}>
        {error && (
          <div className="block-row">
            <div className="block-field error-message">
              {error}
            </div>
          </div>
        )}
        {buttonFirst && button}
        {children}
        {!buttonFirst && button}
        {editing != false && onCancel && <CloseButton onClick={onCancel} />}
      </form>
    </div>
  );
}
